import { useState, useRef } from "react";
import { Banknote, FileText, Send, Loader2, ImagePlus, X, ScanSearch } from "lucide-react";
import { api } from "../../lib/api";
import { extraerDatosComprobante } from "./ocr-comprobante";

interface Props {
  reservaId: number;
  montoSugerido?: number | null;
  instrucciones?: string | null;
  defaults?: {
    nombre?: string;
    telefono?: string;
    email?: string;
    cif?: string;
  };
  origen?: "autoservicio" | "telefono";
  onReportado: () => void;
}

const METODOS = [
  "Pago Móvil",
  "Zelle",
  "Transferencia bancaria",
  "PayPal",
  "Efectivo (depósito)",
  "Otro",
];

export function FormPagoReserva({ reservaId, montoSugerido, instrucciones, defaults, origen = "autoservicio", onReportado }: Props) {
  const [form, setForm] = useState({
    nombrePagador: defaults?.nombre ?? "",
    cedula: defaults?.cif ?? "",
    telefono: defaults?.telefono ?? "",
    email: defaults?.email ?? "",
    montoUsd: montoSugerido ? String(montoSugerido) : "",
    moneda: "USD",
    metodo: "Pago Móvil",
    banco: "",
    numeroReferencia: "",
    fechaPago: new Date().toISOString().slice(0, 10),
    notasCliente: "",
  });
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [enviando, setEnviando] = useState(false);
  const [escaneando, setEscaneando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const seleccionarArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setError("La imagen no puede superar 10MB"); return; }
    setArchivo(f);
    setError(null);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setPreview(ev.target?.result as string);
      reader.readAsDataURL(f);
      // OCR automático
      setEscaneando(true);
      try {
        const datos = await extraerDatosComprobante(f);
        setForm(prev => ({
          ...prev,
          ...(datos.monto && !prev.montoUsd ? { montoUsd: datos.monto } : {}),
          ...(datos.moneda ? { moneda: datos.moneda } : {}),
          ...(datos.referencia && !prev.numeroReferencia ? { numeroReferencia: datos.referencia } : {}),
          ...(datos.fecha ? { fechaPago: datos.fecha } : {}),
          ...(datos.banco && !prev.banco ? { banco: datos.banco } : {}),
          ...(datos.metodo ? { metodo: datos.metodo } : {}),
        }));
      } catch { /* OCR falla silenciosamente */ }
      setEscaneando(false);
    } else {
      setPreview(null);
    }
  };

  const quitarArchivo = () => {
    setArchivo(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const enviar = async () => {
    if (!form.nombrePagador.trim()) { setError("Tu nombre es requerido"); return; }
    if (!form.montoUsd || Number(form.montoUsd) <= 0) { setError("Monto inválido"); return; }
    if (!form.numeroReferencia.trim()) { setError("Número de referencia requerido"); return; }
    setEnviando(true); setError(null);
    try {
      let comprobanteUrl: string | undefined;
      if (archivo) {
        const fd = new FormData();
        fd.append("file", archivo);
        const uploadUrl = origen === "telefono"
          ? `/api/v1/admin/pagos-reservas/upload-comprobante/${reservaId}`
          : `/api/v1/reservas/${reservaId}/upload-comprobante`;
        const upRes = await fetch(uploadUrl, { method: "POST", body: fd });
        if (!upRes.ok) throw new Error("Error al subir el comprobante");
        const upData = await upRes.json();
        comprobanteUrl = upData.url;
      }

      const endpoint = origen === "telefono"
        ? `/v1/admin/pagos-reservas/reportar-telefono/${reservaId}`
        : `/v1/reservas/${reservaId}/reportar-pago`;

      await api(endpoint, {
        method: "POST",
        body: {
          nombrePagador: form.nombrePagador.trim(),
          cedula: form.cedula.trim() || undefined,
          telefono: form.telefono.trim() || undefined,
          email: form.email.trim() || undefined,
          montoUsd: Number(form.montoUsd),
          moneda: form.moneda,
          metodo: form.metodo,
          banco: form.banco.trim() || undefined,
          numeroReferencia: form.numeroReferencia.trim(),
          fechaPago: form.fechaPago,
          notasCliente: form.notasCliente.trim() || undefined,
          comprobanteUrl,
          origen,
        },
      });
      onReportado();
    } catch (e: any) {
      setError(e?.message ?? "Error al reportar el pago. Intentá de nuevo.");
    } finally { setEnviando(false); }
  };

  return (
    <div className="rsv-pago-wrap">
      <style>{STYLES}</style>
      <div className="rsv-pago-card">
        <div className="rsv-pago-head">
          <div className="rsv-pago-ico" aria-hidden>
            <Banknote size={36} strokeWidth={1.7} />
          </div>
          <h2>Confirma tu reserva con un pago</h2>
          <p className="rsv-pago-sub">
            Para asegurar tu lugar necesitamos un pago
            {montoSugerido ? <> equivalente a <b>USD {montoSugerido.toFixed(2)}</b> (en la moneda de tu preferencia)</> : ""}.
            Subí la imagen del comprobante y completá los datos. Una vez que verifiquemos, tu reserva queda confirmada.
          </p>
        </div>

        {instrucciones && (
          <div className="rsv-pago-inst">
            <div className="rsv-pago-inst-titulo">
              <FileText size={13} aria-hidden /> Instrucciones de pago
            </div>
            <div className="rsv-pago-inst-body">{instrucciones}</div>
          </div>
        )}

        <div className="rsv-pago-grid">
          <Field label="Comprobante de pago (imagen)" full>
            <input ref={fileRef} type="file" accept="image/*,.pdf"
              onChange={seleccionarArchivo}
              className="rsv-input" style={{ padding: "8px 10px" }} />
            {escaneando && (
              <div className="rsv-pago-scanning">
                <ScanSearch size={15} className="inline animate-spin" /> Leyendo datos del comprobante…
              </div>
            )}
            {preview && (
              <div className="rsv-pago-preview">
                <img src={preview} alt="Vista previa" />
                <button type="button" onClick={quitarArchivo} className="rsv-pago-preview-x"
                  title="Quitar imagen">
                  <X size={14} />
                </button>
              </div>
            )}
            {archivo && !preview && (
              <div className="rsv-pago-file-badge">
                <FileText size={14} /> {archivo.name}
                <button type="button" onClick={quitarArchivo} className="rsv-pago-preview-x"
                  title="Quitar archivo">
                  <X size={14} />
                </button>
              </div>
            )}
          </Field>

          <Field label="Tu nombre completo *">
            <input className="rsv-input" value={form.nombrePagador}
              onChange={(e) => set("nombrePagador", e.target.value)} />
          </Field>

          <Field label="Cédula / documento">
            <input className="rsv-input" value={form.cedula}
              onChange={(e) => set("cedula", e.target.value.toUpperCase())}
              placeholder="V12345678" />
          </Field>

          <Field label="Teléfono">
            <input className="rsv-input" type="tel" value={form.telefono}
              onChange={(e) => set("telefono", e.target.value)} />
          </Field>

          <Field label="Email">
            <input className="rsv-input" type="email" value={form.email}
              onChange={(e) => set("email", e.target.value)} />
          </Field>

          <Field label="Monto pagado *">
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="rsv-input" type="number" min="0" step="0.01"
                value={form.montoUsd} onChange={(e) => set("montoUsd", e.target.value)}
                style={{ flex: 1 }} />
              <select className="rsv-input" value={form.moneda}
                onChange={(e) => set("moneda", e.target.value)}
                style={{ width: 90, flex: 'none' }}>
                <option value="USD">USD</option>
                <option value="Bs">Bs</option>
                <option value="EUR">EUR</option>
                <option value="COP">COP</option>
              </select>
            </div>
          </Field>

          <Field label="Método de pago *">
            <select className="rsv-input" value={form.metodo}
              onChange={(e) => set("metodo", e.target.value)}>
              {METODOS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>

          <Field label="Banco">
            <input className="rsv-input" value={form.banco}
              onChange={(e) => set("banco", e.target.value)}
              placeholder="Banesco, Mercantil, etc." />
          </Field>

          <Field label="Número de referencia *">
            <input className="rsv-input" value={form.numeroReferencia}
              onChange={(e) => set("numeroReferencia", e.target.value)}
              placeholder="00012345" />
          </Field>

          <Field label="Fecha del pago" full>
            <input className="rsv-input" type="date" value={form.fechaPago}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => set("fechaPago", e.target.value)} />
          </Field>

          <Field label="Notas adicionales (opcional)" full>
            <textarea className="rsv-input" rows={2} value={form.notasCliente}
              onChange={(e) => set("notasCliente", e.target.value)}
              placeholder="Cualquier detalle que nos quieras aclarar…" />
          </Field>
        </div>

        {error && <div className="rsv-pago-error">{error}</div>}

        <button onClick={enviar} disabled={enviando}
          className="rsv-pago-btn">
          {enviando ? (
            <><Loader2 size={16} className="inline animate-spin" /> Enviando…</>
          ) : (
            <><Send size={16} className="inline" /> Reportar pago</>
          )}
        </button>

        <p className="rsv-pago-foot">
          Verificamos el pago manualmente y te confirmamos por WhatsApp/email cuando esté listo.
        </p>
      </div>
    </div>
  );
}

function Field({ label, full, children }: { label: string; full?: boolean; children: React.ReactNode }) {
  return (
    <div className={full ? "rsv-pago-field rsv-pago-field-full" : "rsv-pago-field"}>
      <label>{label}</label>
      {children}
    </div>
  );
}

const STYLES = `
.rsv-pago-wrap { max-width: 640px; margin: 0 auto; padding: 16px; }
.rsv-pago-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  padding: 24px; border: 1px solid #e5e7eb;
}
.rsv-pago-head { text-align: center; margin-bottom: 20px; }
.rsv-pago-ico {
  display: inline-flex; align-items: center; justify-content: center;
  width: 64px; height: 64px; border-radius: 18px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #059669; margin: 0 auto;
  box-shadow: 0 6px 20px rgba(16,185,129,0.18);
}
.rsv-pago-head h2 { font-size: 22px; font-weight: 700; color: #111; margin: 10px 0 6px; font-family: Georgia, serif; }
.rsv-pago-sub { font-size: 14px; color: #555; line-height: 1.55; }
.rsv-pago-inst {
  background: #fff7ed; border: 1px solid #fed7aa;
  border-radius: 12px; padding: 12px; margin-bottom: 16px;
}
.rsv-pago-inst-titulo {
  font-size: 12px; font-weight: 600; color: #9a3412; margin-bottom: 4px;
  display: inline-flex; align-items: center; gap: 5px;
}
.rsv-pago-inst-body { font-size: 13px; color: #333; white-space: pre-wrap; line-height: 1.5; }
.rsv-pago-grid {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 12px; margin-bottom: 16px;
}
.rsv-pago-field { display: flex; flex-direction: column; }
.rsv-pago-field-full { grid-column: 1 / -1; }
.rsv-pago-field label {
  font-size: 11px; font-weight: 600; color: #666;
  text-transform: uppercase; letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.rsv-input {
  width: 100%; padding: 11px 13px; border-radius: 10px;
  border: 1.5px solid #d1d5db; font-size: 16px;
  background: #fff; color: #111; font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.rsv-input:focus {
  outline: 2px solid rgba(16,185,129,0.4); outline-offset: 1px;
  border-color: #10b981;
}
.rsv-pago-error {
  background: #fee2e2; color: #991b1b; padding: 10px;
  border-radius: 8px; font-size: 13px; margin-bottom: 12px;
}
.rsv-pago-btn {
  width: 100%; padding: 14px;
  background: linear-gradient(135deg, #10b981, #059669);
  color: #fff; border-radius: 12px; font-weight: 700;
  font-size: 15px; border: none; cursor: pointer;
  box-shadow: 0 4px 12px rgba(16,185,129,0.3);
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  transition: transform .12s ease, box-shadow .15s ease, opacity .15s;
  min-height: 48px;
}
.rsv-pago-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(16,185,129,0.42);
}
.rsv-pago-btn:not(:disabled):active { transform: translateY(0); }
.rsv-pago-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.rsv-pago-btn .animate-spin { animation: rsvPagoSpin 0.9s linear infinite; }
@keyframes rsvPagoSpin { to { transform: rotate(360deg); } }
.rsv-pago-foot {
  text-align: center; font-size: 11px;
  color: #999; margin-top: 12px; line-height: 1.5;
}
.rsv-pago-preview {
  position: relative; display: inline-block; margin-top: 8px;
}
.rsv-pago-preview img {
  max-width: 100%; max-height: 180px; border-radius: 10px;
  border: 1.5px solid #d1d5db; object-fit: contain;
}
.rsv-pago-preview-x {
  position: absolute; top: -6px; right: -6px;
  width: 22px; height: 22px; border-radius: 50%;
  background: #ef4444; color: #fff; border: 2px solid #fff;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer; padding: 0;
}
.rsv-pago-file-badge {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px; padding: 6px 10px; border-radius: 8px;
  background: #f3f4f6; font-size: 13px; color: #555;
  position: relative;
}
.rsv-pago-scanning {
  display: inline-flex; align-items: center; gap: 6px;
  margin-top: 8px; padding: 8px 12px; border-radius: 8px;
  background: #eff6ff; color: #1d4ed8; font-size: 13px;
  font-weight: 500; border: 1px solid #bfdbfe;
}
@media (max-width: 600px) {
  .rsv-pago-grid { grid-template-columns: 1fr; }
}
`;
